import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Status Badge Cell Renderer
 *
 * Renders status values as colored badges with customizable colors and labels.
 *
 * @example
 * ```typescript
 * columnDefs: ColDef<Order>[] = [
 *   {
 *     field: 'statusId',
 *     headerName: 'Estado',
 *     cellRenderer: StatusBadgeRendererComponent,
 *     cellRendererParams: {
 *       statusMap: {
 *         0: { label: 'Anulado', color: 'red' },
 *         1: { label: 'Activo', color: 'green' },
 *         6: { label: 'En Progreso', color: 'blue' },
 *         7: { label: 'Aprobado', color: 'purple' },
 *         8: { label: 'Rechazado', color: 'red' },
 *         18: { label: 'Inactivo', color: 'gray' },
 *       }
 *     }
 *   }
 * ];
 * ```
 */
@Component({
  selector: 'app-status-badge-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span
      *ngIf="statusConfig"
      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
      [ngClass]="getBadgeClasses()">
      {{ statusConfig.label || params.value }}
    </span>
    <span *ngIf="!statusConfig" class="text-gray-500 text-xs">
      {{ params.value || 'N/A' }}
    </span>
  `,
})
export class StatusBadgeRendererComponent implements CellRenderer<any> {
  @Input() params!: CellRendererParams<any>;

  statusConfig?: { label: string; color: string };

  ngOnInit(): void {
    this.updateStatusConfig();
  }

  ngOnChanges(): void {
    this.updateStatusConfig();
  }

  private updateStatusConfig(): void {
    const statusMap = this.params.colDef?.cellRendererParams?.statusMap || {};
    this.statusConfig = statusMap[this.params.value];
  }

  getBadgeClasses(): string {
    if (!this.statusConfig) return '';

    const colorClasses: Record<string, string> = {
      red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      gray: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
      indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
    };

    return colorClasses[this.statusConfig.color] || colorClasses['gray'];
  }
}
